
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/hooks/use-toast';

interface Account {
  account_id: string;
  account_name: string;
  is_owner: boolean;
}

interface AccountContextType {
  currentAccount: Account | null;
  accounts: Account[];
  setCurrentAccount: (account: Account) => void;
  isLoading: boolean;
  error: Error | null;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export function AccountProvider({ children }: { children: ReactNode }) {
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchAttempts, setFetchAttempts] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const fetchAccounts = async () => {
      console.log('[AccountContext] Starting fetchAccounts, attempt:', fetchAttempts + 1);
      
      try {
        setError(null);
        setFetchAttempts(prev => prev + 1);
        
        const { data: { session } } = await supabase.auth.getSession();
        console.log('[AccountContext] Session check:', !!session?.user?.id);
        
        if (!session?.user?.id) {
          console.log('[AccountContext] No active session, redirecting to auth');
          if (isMounted) {
            setIsLoading(false);
            navigate('/auth');
          }
          return;
        }

        console.log('[AccountContext] Fetching accounts for user:', session.user.id);

        // Use a timeout to prevent hanging
        const fetchPromise = supabase
          .from('accounts')
          .select('account_id, account_name, is_owner')
          .order('created_at', { ascending: false });

        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Account fetch timeout')), 10000);
        });

        const { data: accountsData, error: accountsError } = await Promise.race([
          fetchPromise,
          timeoutPromise
        ]) as any;

        if (timeoutId) clearTimeout(timeoutId);

        if (accountsError) {
          throw accountsError;
        }

        console.log('[AccountContext] Accounts fetched:', accountsData?.length || 0);

        if (!isMounted) return;

        if (!accountsData || accountsData.length === 0) {
          console.log('[AccountContext] No accounts found, creating default account');
          
          const { data: newAccount, error: createError } = await supabase
            .from('accounts')
            .insert([
              { 
                account_name: 'My Account',
                is_owner: true,
                user_id: session.user.id
              }
            ])
            .select('account_id, account_name, is_owner')
            .maybeSingle();

          if (createError) {
            throw createError;
          }

          if (newAccount && isMounted) {
            console.log('[AccountContext] Default account created:', newAccount);
            setAccounts([newAccount]);
            setCurrentAccount(newAccount);
          }
        } else {
          if (isMounted) {
            setAccounts(accountsData);
            if (!currentAccount || !accountsData.find((a: Account) => a.account_id === currentAccount.account_id)) {
              setCurrentAccount(accountsData[0]);
            }
          }
        }
      } catch (error: any) {
        console.error('[AccountContext] Error in fetchAccounts:', error);
        if (isMounted) {
          const errorObj = error instanceof Error ? error : new Error(error?.message || 'Failed to load accounts');
          setError(errorObj);
          
          // Only show toast for non-timeout errors
          if (!error?.message?.includes('timeout')) {
            toast({
              title: "Account Error",
              description: errorObj.message,
              variant: "destructive",
            });
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAccounts();

    // Set up real-time subscription with error handling
    const accountSubscription = supabase
      .channel('accounts_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'accounts' 
      }, (payload) => {
        console.log('[AccountContext] Real-time update received:', payload);
        if (isMounted) {
          fetchAccounts();
        }
      })
      .subscribe((status) => {
        console.log('[AccountContext] Subscription status:', status);
      });

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      accountSubscription.unsubscribe();
    };
  }, [navigate, toast]);

  // Provide context value with fallbacks
  const value = {
    currentAccount: currentAccount || null,
    accounts: accounts || [],
    setCurrentAccount,
    isLoading,
    error
  };

  console.log('[AccountContext] Providing context:', {
    hasCurrentAccount: !!currentAccount,
    accountsCount: accounts.length,
    isLoading,
    hasError: !!error,
    fetchAttempts
  });

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};
